import "./EditServiceAssignees.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { ControlledDateInput } from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { KaboodleToaster } from "components/common";
import { Select } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import {
  useDeleteServiceByIdMutation,
  useGetArchivedServicesQuery,
  usePatchServiceByIdMutation,
  useRestoreServiceMutation,
} from "services/service-api.ts";
import {
  useCreateServiceAssigneeMutation,
  useDeleteServiceIdMutation,
  useGetServiceAssigneesQuery,
  useUpdateServiceAssigneeMutation,
} from "services/service-assignee-api.ts";
import type { ServiceType } from "types/DataTypes";
import { extractErrorMessage } from "utils/error-utils";
import { getVerboseServiceStatus } from "utils/format-utils";
import { useSelector } from "utils/redux";

const FAKED_MEMBER = { id: 0, label: "", value: 0 };

type Member = {
  label: string;
  value: number;
  id: number;
};

type SelectOption = {
  label: string;
  value: number;
  roles?: string[];
};

type EditServiceAssigneesProps = {
  fileServices?: ServiceType[];
  fileId?: number;
  isReadOnly?: boolean;
};

const MemoizedSelect = React.memo(Select);

const ServiceBadge = React.memo(({ status }: { status: string }) => (
  <div className="EditTeam__Service_Header__Status">
    {getVerboseServiceStatus(status)}
  </div>
));

const ServiceHeader = React.memo(
  ({
    service,
    title,
    onNavigate,
    canDelete = false,
    onDelete,
  }: {
    service: ServiceType;
    title: string;
    onNavigate: () => void;
    canDelete?: boolean;
    onDelete?: () => void;
  }) => {
    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();

      // check if service is accessible
      if (
        !service?.status ||
        service.status === "U" ||
        !service?.workspace_url
      ) {
        return;
      }

      window.open(service.workspace_url, "_blank");
    };

    return (
      <div className="EditTeam__Service_Header">
        <a
          className="EditTeam__Service_Header__Title"
          onClick={onNavigate}
          onContextMenu={handleContextMenu}
        >
          {title}
        </a>
        <div
          className="EditTeam__Service_Header__Button material-icons"
          onClick={onNavigate}
          onContextMenu={handleContextMenu}
        >
          link
        </div>

        <ServiceBadge status={service.status} />

        {canDelete && (
          <button
            type="button"
            className="EditTeam__Service_Header__Delete material-icons"
            title="Delete service"
            aria-label="Delete service"
            onClick={(e) => {
              e.preventDefault();
              onDelete?.();
            }}
          >
            delete
          </button>
        )}
      </div>
    );
  },
);

const MemberRow = React.memo(
  ({
    member,
    options,
    isFirst,
    isReadOnly = false,
    onAdd,
    onDelete,
    onChange,
  }: {
    member: Member;
    options: SelectOption[];
    isFirst: boolean;
    isReadOnly?: boolean;
    onAdd: () => void;
    onDelete: () => void;
    onChange: (id: number, option: SelectOption) => void;
  }) => {
    return (
      <div className="EditTeam__Service_Member">
        <MemoizedSelect
          name="service"
          label="Team member"
          classNames="select-sub-list"
          options={options}
          isClearable={false}
          readOnly={isReadOnly}
          value={{
            label: member.label,
            value: member.value,
          }}
          onChange={(o) =>
            !isReadOnly && onChange(member.id, o as SelectOption)
          }
          containerClassNames="w-buttons"
        />
        <figure className="buttons-container-add-delete">
          <div className="flex jc-end">
            {!isReadOnly && (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  onAdd();
                }}
                className="btn add"
              >
                <span className="material-icons">add</span>
              </a>
            )}
            {!isFirst && !isReadOnly && (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  onDelete();
                }}
                className="btn delete"
              >
                <span className="material-icons">delete</span>
              </a>
            )}
          </div>
        </figure>
      </div>
    );
  },
);

const TeamServiceForm = React.memo(
  ({
    service,
    isReadOnly = false,
    canDelete = false,
    onRequestDelete,
  }: {
    service: ServiceType;
    isReadOnly?: boolean;
    canDelete?: boolean;
    onRequestDelete?: (service: ServiceType) => void;
  }) => {
    const [members, setMembers] = useState<Member[]>([FAKED_MEMBER]);
    const constants = useSelector((state) => state.shared.constants);
    const navigate = useNavigate();

    const {
      control,
      setValue,
      getValues,
      formState: { errors },
    } = useForm({
      defaultValues: {
        client_materials_fully_received_on:
          service.client_materials_fully_received_on || "",
      },
    });

    const [patchServiceById] = usePatchServiceByIdMutation();

    // update form when service data changes
    useEffect(() => {
      setValue(
        "client_materials_fully_received_on",
        service.client_materials_fully_received_on || "",
      );
    }, [service.client_materials_fully_received_on, setValue]);

    const handleSaveServiceField = useCallback(
      async (
        field: string,
        value: string | number | boolean | null | undefined,
      ) => {
        if (service.id) {
          try {
            await patchServiceById({
              id: service.id,
              data: {
                [field]: value,
              },
            }).unwrap();
          } catch (error) {
            console.error("Error saving service field:", error);
            KaboodleToaster.error("Failed to save changes");
          }
        }
      },
      [patchServiceById, service.id],
    );

    const getTitle = useCallback(
      (label: string) =>
        constants?.SERVICE_TYPE.find((s) => s.value === label)?.label,
      [constants?.SERVICE_TYPE],
    );

    const [createServiceAssignee] = useCreateServiceAssigneeMutation();
    const [deleteServiceAssignee] = useDeleteServiceIdMutation();
    const [updateServiceAssignee] = useUpdateServiceAssigneeMutation();

    const {
      data: serviceAssigneesData = {
        results: [],
        count: 0,
        next: null,
        previous: null,
      },
    } = useGetServiceAssigneesQuery(
      {
        serviceId: service.value.toString(),
      },
      {
        skip: !service || !service.id,
      },
    );

    const serviceAssignees = useMemo(
      () =>
        serviceAssigneesData.results?.map((assignee) => ({
          ...assignee,
          label:
            assignee.user_role?.length > 0
              ? `${assignee.user_full_name} (${assignee.user_role.join(", ")})`
              : assignee.user_full_name,
        })),
      [serviceAssigneesData],
    );

    const filteredOptions = useMemo(() => {
      if (!constants?.USERS || !serviceAssignees) return [];

      const alreadyAssignedIds = serviceAssignees.map((u) => u.user);
      return constants.USERS.filter(
        (u) => !alreadyAssignedIds.includes(u.value),
      ).map((assignee) => ({
        ...assignee,
        label:
          assignee.roles?.length > 0
            ? `${assignee.label} (${assignee.roles.join(", ")})`
            : assignee.label,
      }));
    }, [serviceAssignees, constants?.USERS]);

    useEffect(() => {
      if (serviceAssignees) {
        const mappedMembers = serviceAssignees.map((assignee) => ({
          label: assignee.label,
          value: assignee.user,
          id: assignee.id,
        }));

        // always show at least one row (FAKED_MEMBER) to allow adding first assignee
        setMembers(mappedMembers.length > 0 ? mappedMembers : [FAKED_MEMBER]);
      }
    }, [serviceAssignees]);

    const handleCreateServiceAssignee = useCallback(
      async (option: SelectOption) => {
        try {
          await createServiceAssignee({
            data: {
              service: service.value,
              user: option.value,
            },
          }).unwrap();
        } catch (error) {
          const errorMessage = extractErrorMessage(
            error,
            "Failed to add team member",
          );
          KaboodleToaster.error(errorMessage);
        }
      },
      [createServiceAssignee, service.value],
    );

    const handleDeleteServiceAssignee = useCallback(
      async (id: number) => {
        if (!id) return;

        try {
          await deleteServiceAssignee({
            id: id.toString(),
          }).unwrap();
        } catch (error) {
          const errorMessage = extractErrorMessage(
            error,
            "Failed to remove team member",
          );
          KaboodleToaster.error(errorMessage);
        }
      },
      [deleteServiceAssignee],
    );

    const handleUpdateServiceAssignee = useCallback(
      async (assigneeId: number, newUser: SelectOption) => {
        if (!assigneeId) return;

        try {
          const result = await updateServiceAssignee({
            assigneeId: assigneeId.toString(),
            userId: newUser.value,
          }).unwrap();

          // Show info toast if task reassignment was skipped
          if (result?.warning && typeof result.warning === "string") {
            KaboodleToaster.info(result.warning);
          }
        } catch (error) {
          const errorMessage = extractErrorMessage(
            error,
            "An unexpected error occurred",
          );
          KaboodleToaster.error(errorMessage);
        }
      },
      [updateServiceAssignee],
    );

    const navigateToService = useCallback(() => {
      // no status available
      if (!service?.status) {
        KaboodleToaster.info("Service status is unavailable");
        return;
      }

      // service is unprocessed
      if (service.status === "U") {
        KaboodleToaster.info("Service has not been processed yet");
        return;
      }

      // no workspace URL available
      if (!service?.workspace_url) {
        KaboodleToaster.info("Service workspace is not available yet");
        return;
      }

      navigate(service.workspace_url);
    }, [navigate, service?.status, service?.workspace_url]);

    const handleChange = useCallback(
      (id: number, option: SelectOption) => {
        if (id) {
          handleUpdateServiceAssignee(id, option);
        } else {
          handleCreateServiceAssignee(option);
        }
      },
      [handleUpdateServiceAssignee, handleCreateServiceAssignee],
    );

    const handleAddMember = useCallback(() => {
      setMembers((prevMembers) => [...prevMembers, FAKED_MEMBER]);
    }, []);

    const handleDeleteMember = useCallback(
      async (id: number) => {
        await handleDeleteServiceAssignee(id);
        // Note: UI will update automatically via RTK Query cache invalidation
        // No need to manually update members state
      },
      [handleDeleteServiceAssignee],
    );

    const title = useMemo(
      () => getTitle(service?.label),
      [getTitle, service?.label],
    );

    return (
      <>
        <ServiceHeader
          service={service}
          title={title}
          onNavigate={navigateToService}
          canDelete={canDelete}
          onDelete={() => onRequestDelete?.(service)}
        />

        {members.map((member, index) => (
          <MemberRow
            key={`${member.id || "new"}-${index}`}
            member={member}
            options={filteredOptions}
            isFirst={index === 0}
            isReadOnly={isReadOnly}
            onAdd={handleAddMember}
            onDelete={() => handleDeleteMember(member.id)}
            onChange={handleChange}
          />
        ))}

        <ControlledDateInput
          classNames="ServiceAssignees__DateInput"
          control={control}
          name="client_materials_fully_received_on"
          label="Materials Fully Received On"
          errors={errors}
          readOnly={isReadOnly}
          onSave={() =>
            handleSaveServiceField(
              "client_materials_fully_received_on",
              getValues("client_materials_fully_received_on"),
            )
          }
          allowFutureDates={true}
        />
      </>
    );
  },
);

const ArchivedServiceRow = React.memo(
  ({
    service,
    onRestore,
    isRestoring,
  }: {
    service: ServiceType;
    onRestore: (service: ServiceType) => void;
    isRestoring: boolean;
  }) => {
    const constants = useSelector((state) => state.shared.constants);
    const title = useMemo(
      () =>
        constants?.SERVICE_TYPE.find((s) => s.value === service.label)?.label ||
        service.label,
      [constants?.SERVICE_TYPE, service.label],
    );

    return (
      <div className="EditTeam__Archived_Row">
        <span className="EditTeam__Archived_Row__Title">{title}</span>
        <Button
          className="allan-koba"
          onClick={() => onRestore(service)}
          disabled={isRestoring}
        >
          {isRestoring ? "Restoring..." : "Restore"}
        </Button>
      </div>
    );
  },
);

const EditServiceAssignees = React.memo(
  ({ fileServices, fileId, isReadOnly = false }: EditServiceAssigneesProps) => {
    const { data: orgSettings } = useGetOrganizationSettingsQuery();
    const location = useLocation();
    const isFutureMedicalPage = location.pathname.includes("/future-medical");

    const constants = useSelector((state) => state.shared.constants);
    const canManage = useSelector(
      (state) => !!state.main.loggedInUser?.has_management_authority,
    );
    const canManageServices = canManage && !isReadOnly;

    const [showArchived, setShowArchived] = useState(false);
    const [serviceToDelete, setServiceToDelete] = useState<ServiceType | null>(
      null,
    );
    // Track every service currently being restored so restoring two services
    // at once each keeps its own "Restoring..." state.
    const [restoringIds, setRestoringIds] = useState<Set<number>>(
      () => new Set(),
    );

    const [deleteServiceById, { isLoading: isDeleting }] =
      useDeleteServiceByIdMutation();
    const [restoreService] = useRestoreServiceMutation();

    const { data: archivedServices = [] } = useGetArchivedServicesQuery(
      { fileId: fileId as number },
      { skip: !canManageServices || !showArchived || !fileId },
    );

    const handleConfirmDelete = useCallback(async () => {
      if (!serviceToDelete) return;
      try {
        await deleteServiceById({ id: serviceToDelete.id }).unwrap();
        KaboodleToaster.success("Service deleted");
        setServiceToDelete(null);
      } catch (error) {
        const errorMessage = extractErrorMessage(
          error,
          "Failed to delete service",
        );
        KaboodleToaster.error(errorMessage);
      }
    }, [deleteServiceById, serviceToDelete]);

    const handleRestore = useCallback(
      async (service: ServiceType) => {
        setRestoringIds((prev) => new Set(prev).add(service.id));
        try {
          await restoreService({ id: service.id }).unwrap();
          KaboodleToaster.success("Service restored");
        } catch (error) {
          const errorMessage = extractErrorMessage(
            error,
            "Failed to restore service",
          );
          KaboodleToaster.error(errorMessage);
        } finally {
          setRestoringIds((prev) => {
            const next = new Set(prev);
            next.delete(service.id);
            return next;
          });
        }
      },
      [restoreService],
    );

    const CardComponent = isFutureMedicalPage ? FutureMedicalCard : Card;

    const serviceToDeleteTitle = serviceToDelete
      ? constants?.SERVICE_TYPE.find((s) => s.value === serviceToDelete.label)
          ?.label || serviceToDelete.label
      : "";

    const archiveToggle = canManageServices ? (
      <button
        type="button"
        className="EditTeam__ArchiveToggle"
        onClick={(e) => {
          e.preventDefault();
          setShowArchived((prev) => !prev);
        }}
      >
        <span className="material-icons">
          {showArchived ? "visibility_off" : "history"}
        </span>
        {showArchived ? "Hide archived" : "Show archived"}
      </button>
    ) : undefined;

    return (
      <>
        <CardComponent
          heading={`${orgSettings?.name || ""} Team Selection`}
          cardNumber={1}
          classNames="EditTeam"
          headerButton={archiveToggle}
        >
          {fileServices?.map((service: ServiceType) => (
            <TeamServiceForm
              key={service.value}
              service={service}
              isReadOnly={isReadOnly}
              canDelete={canManageServices}
              onRequestDelete={setServiceToDelete}
            />
          ))}

          {showArchived && canManageServices && (
            <div className="EditTeam__Archived">
              <div className="EditTeam__Archived__Heading">
                Archived Services
              </div>
              {archivedServices.length === 0 ? (
                <p className="EditTeam__Archived__Empty">
                  No archived services for this file.
                </p>
              ) : (
                archivedServices.map((service) => (
                  <ArchivedServiceRow
                    key={service.id}
                    service={service}
                    onRestore={handleRestore}
                    isRestoring={restoringIds.has(service.id)}
                  />
                ))
              )}
            </div>
          )}
        </CardComponent>

        <KaboodleModal
          isOpen={!!serviceToDelete}
          onClose={() => !isDeleting && setServiceToDelete(null)}
          footer={
            <div className="flex jc-c">
              <Button
                className="delete"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button
                onClick={() => setServiceToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Delete Service">
            <p>
              Are you sure you want to delete the{" "}
              <strong>{serviceToDeleteTitle}</strong> service? It will be
              archived and can be restored later.
            </p>
          </Card>
        </KaboodleModal>
      </>
    );
  },
);

export default EditServiceAssignees;
